import { ConnectionIcon } from "@minervaproject/fds";
import React from "react";

import { ExpandableTableRow } from "./expandable_table_row";

import { useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useUserProperties } from "hooks/use_user_properties";
import { Settings } from "settings";
import {
  MonitoringEventTypeType,
  contextDataKeyType,
  contextDataValueType,
} from "shared/types/monitoring_event";
import { selectUserHasPoorConnection } from "store/selectors/classroom";
import {
  selectAvRTTForUser,
  selectLatestPubsubStatsByUserId,
  selectMonitoringEventsByUserIdAndTypes,
} from "store/selectors/monitoring";
import { formatDateTime } from "utils/date";

const EVENT_TYPE_TO_CONTEXT_DATA_KEYS_MAP: Partial<
  Record<MonitoringEventTypeType, contextDataKeyType[]>
> = {
  [MonitoringEventTypeType.AV_REFRESH]: ["reason"],
  [MonitoringEventTypeType.FAILED_CONNECTION_ALERT]: ["reason"],
  [MonitoringEventTypeType.FAILED_API_REQUEST]: ["urlPath", "method"],
  [MonitoringEventTypeType.RELOAD_STREAM]: ["reason"],
};
const CONNECTION_EVENT_TYPES = Object.keys(
  EVENT_TYPE_TO_CONTEXT_DATA_KEYS_MAP,
) as MonitoringEventTypeType[];

const FDS_RED = "#DF2F26";

function UserConnection({ userId }: { userId: string }) {
  const user = useUserProperties(userId, ["bandwidth_mode"]);
  const hasPoorConnection = useAppSelector(selectUserHasPoorConnection(userId));
  const connectionEvents = useArraySelector(
    selectMonitoringEventsByUserIdAndTypes(userId, CONNECTION_EVENT_TYPES),
    ["id"],
  );
  const pubsubStats = useAppSelector(selectLatestPubsubStatsByUserId(userId));
  const pingRTT = pubsubStats?.contextData.pingRTT;
  const medianPingRTT = pingRTT?.median || 0;
  const formattedPingRTT = pingRTT ? `(${pingRTT.min}, ${pingRTT.median}, ${pingRTT.max})` : "";
  const avRTT = useAppSelector(selectAvRTTForUser(userId));

  if (!user) return null;

  return (
    <div className="mb10" data-testid="user-connection">
      <h2 className="h2 mxn6">
        <ConnectionIcon width={16} height={16} />
        <span className="ml2">Connection</span>
      </h2>
      <div className="flex items-center my3 body-s">
        <div
          className={
            medianPingRTT > Settings.PUBSUB_PING_DANGER_THRESHOLD
              ? "bg-red-tint-90 text-red px2 py1 mr3"
              : medianPingRTT > Settings.PUBSUB_PING_WARNING_THRESHOLD
                ? "bg-yellow-tint-90 text-yellow px2 py1 mr3"
                : "mr3"
          }
          style={{ borderRadius: "4px" }}
        >
          Ping: {formattedPingRTT}
        </div>
        <div className="mr3">RTT: {avRTT || "N/A"}</div>
        <div className="mr3">
          Bandwidth Mode:{" "}
          <span
            className={user.bandwidth_mode === "high" ? "text-green" : "text-red"}
            style={{ textTransform: "capitalize" }}
          >
            {user.bandwidth_mode}
          </span>
        </div>
        <div>
          <span className={hasPoorConnection ? "text-red" : ""}>
            PC Indicator: {hasPoorConnection ? "Yes" : "No"}
          </span>
        </div>
      </div>
      {connectionEvents.length ? (
        <table className="fds-table">
          <thead>
            <tr>
              {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
              <th className="col-1" />
              <th className="col-5">Error Event</th>
              <th className="col-6">Time</th>
            </tr>
          </thead>
          <tbody>
            {connectionEvents.map((event) => {
              const contextData = event.contextData as {
                [k in contextDataKeyType]: contextDataValueType;
              };
              const contextDataKeys = EVENT_TYPE_TO_CONTEXT_DATA_KEYS_MAP[event.type];
              const expandedDetails = (
                <div>
                  {contextDataKeys?.map((key) => (
                    <React.Fragment key={key}>
                      <div className="bold">{key}:</div>
                      <div className="mb2">{`${contextData[key]}`}</div>
                    </React.Fragment>
                  ))}
                </div>
              );
              return (
                <ExpandableTableRow
                  key={event.id}
                  expandedContent={expandedDetails}
                  colSpan={2}
                  iconColor={FDS_RED}
                >
                  <td className="col-5 text-red">{event.type}</td>
                  <td className="col-6">{formatDateTime(event.timestamp, true)}</td>
                </ExpandableTableRow>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="my3 body-s">No connection errors exist.</div>
      )}
    </div>
  );
}

export { UserConnection };
